import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';

export const TRUSTPILOT_REVIEW_URL = 'https://www.trustpilot.com/review/eladoreruffles.com';

const TRUSTPILOT_SCRIPT_SRC = 'https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js';
const TRUSTPILOT_TEMPLATE_ID = '5419b6a8b0d04a076446a9ad';
const TRUSTPILOT_BUSINESS_UNIT_ID = '6249928f7af6703df12b442c';
const TRUSTPILOT_GREEN = '#00B67A';

const isTrustpilotEnabled = () => (
  process.env.NODE_ENV === 'production' ||
  String(process.env.REACT_APP_ENABLE_TRUSTPILOT).toLowerCase() === 'true'
);

const TrustpilotWidget = () => {
  const { t } = useTranslation();
  const widgetRef = useRef(null);
  const enabled = isTrustpilotEnabled();

  useEffect(() => {
    if (!enabled || !widgetRef.current) {
      return undefined;
    }

    let intervalId;
    let timeoutId;
    let observer;
    let script;
    let cancelled = false;
    let widgetLoaded = false;
    let bootStarted = false;

    const stopWaiting = () => {
      if (intervalId) {
        window.clearInterval(intervalId);
        intervalId = undefined;
      }
      if (timeoutId) {
        window.clearTimeout(timeoutId);
        timeoutId = undefined;
      }
    };

    const loadWidget = () => {
      if (widgetLoaded) {
        return true;
      }
      if (cancelled || !widgetRef.current || !window.Trustpilot?.loadFromElement) {
        return false;
      }

      window.Trustpilot.loadFromElement(widgetRef.current, true);
      widgetLoaded = true;
      return true;
    };

    const waitForTrustpilot = () => {
      if (loadWidget()) {
        stopWaiting();
        return;
      }
      if (intervalId) {
        return;
      }

      intervalId = window.setInterval(() => {
        if (loadWidget()) {
          stopWaiting();
        }
      }, 100);

      timeoutId = window.setTimeout(() => {
        stopWaiting();
      }, 5000);
    };

    const handleScriptError = () => console.error('Failed to load Trustpilot script.');

    const bootWidget = () => {
      if (bootStarted || cancelled) {
        return;
      }
      bootStarted = true;

      script = document.querySelector(`script[src="${TRUSTPILOT_SCRIPT_SRC}"]`);

      if (!script) {
        script = document.createElement('script');
        script.src = TRUSTPILOT_SCRIPT_SRC;
        script.type = 'text/javascript';
        script.async = true;
        document.body.appendChild(script);
      }

      script.addEventListener('load', waitForTrustpilot);
      script.addEventListener('error', handleScriptError);
      waitForTrustpilot();
    };

    const handleIntersection = (entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) {
        return;
      }

      observer?.disconnect();
      observer = undefined;
      bootWidget();
    };

    if (typeof window.IntersectionObserver === 'function') {
      try {
        observer = new window.IntersectionObserver(handleIntersection, {
          rootMargin: '300px 0px',
        });
        observer.observe(widgetRef.current);
      } catch {
        bootWidget();
      }
    } else {
      bootWidget();
    }

    return () => {
      cancelled = true;
      observer?.disconnect();
      if (script) {
        script.removeEventListener('load', waitForTrustpilot);
        script.removeEventListener('error', handleScriptError);
      }
      stopWaiting();
    };
  }, [enabled]);

  return (
    <Box
      data-testid="trustpilot-footer-widget"
      sx={{
        width: '100%',
        maxWidth: 300,
        minHeight: 24,
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
        '& iframe': {
          display: 'block',
        },
      }}
    >
      <div
        ref={widgetRef}
        id="trustpilot-footer-widget"
        className="trustpilot-widget"
        data-locale="en-GB"
        data-template-id={TRUSTPILOT_TEMPLATE_ID}
        data-businessunit-id={TRUSTPILOT_BUSINESS_UNIT_ID}
        data-style-height="24px"
        data-style-width="100%"
        data-theme="dark"
        style={{ width: '100%', minHeight: 24 }}
      >
        <Box
          component="a"
          href={TRUSTPILOT_REVIEW_URL}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={t('nav.trustpilotAria')}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '6px',
            color: 'rgba(244, 241, 234, 0.8)',
            fontSize: '0.75rem',
            lineHeight: 1.4,
            textDecoration: 'none',
            '&:hover': { color: '#FFFFFF' },
          }}
        >
          <Box component="span" aria-hidden="true" sx={{ color: TRUSTPILOT_GREEN, fontSize: '0.9rem', lineHeight: 1 }}>
            ★
          </Box>
          <Box component="strong" sx={{ fontWeight: 700 }}>
            Trustpilot
          </Box>
          <Box component="span" aria-hidden="true" sx={{ color: TRUSTPILOT_GREEN, letterSpacing: '1px' }}>
            ★★★★★
          </Box>
          <Box component="span" sx={{ color: 'rgba(244, 241, 234, 0.55)' }}>
            {t('footer.readOurReviews')}
          </Box>
        </Box>
      </div>
    </Box>
  );
};

export default TrustpilotWidget;
